import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Star as StarIcon,
  Tv as PresenterIcon,
  ContentCopyRounded as ContentCopyIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { 
  StyledDrawer, 
  DrawerContent, 
  MenuHeader, 
  ContentSection,
  CreatorBadge,
  PresenterBadge,
  LeaveRoomButton,
  scrollbarStyles 
} from '../shared/StyledComponents';
import { styled } from '@mui/material/styles';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  display: 'flex',
  alignItems: 'center',
  minHeight: '48px',
}));

const PlayersList = styled(List)(({ theme }) => ({
  maxHeight: '300px',
  overflow: 'auto',
  ...scrollbarStyles(theme)
}));

const LobbyDrawer = ({
  isOpen,
  onClose,
  roomId,
  isCreator,
  connectedUsers,
  onCreateRoom,
  onLeaveRoom,
  onCopyRoomLink,
  roomCode,
  setRoomCode,
  showJoinFields,
  setShowJoinFields,
  onJoinRoom,
}) => {

  const copyRoomLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}?room=${roomId}`);
    onCopyRoomLink?.();
  };

  return (
    <StyledDrawer anchor="left" open={isOpen} onClose={onClose}>
      <DrawerContent>
        <MenuHeader>
          <Typography>Lobby</Typography>
        </MenuHeader>

        <ContentSection textAlign="center">
          {roomId ? (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" sx={{ color: 'text.primary' }}>
                  Room: {roomId}
                </Typography>
                <Tooltip title="Copy Room Link">
                  <IconButton size="small" onClick={copyRoomLink}>
                    <ContentCopyIcon color="secondary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <LeaveRoomButton
                fullWidth
                variant="contained"
                onClick={onLeaveRoom}
              >
                {isCreator ? "Close Room" : "Leave Room"}
              </LeaveRoomButton>

              {isCreator && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Game state will be saved so you can continue the adventure later
                </Typography>
              )}
            </>
          ) : (
            <>
              <List>
                <StyledListItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onCreateRoom}
                  >
                    Create Room
                  </Button>
                </StyledListItem>

                <StyledListItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowJoinFields(!showJoinFields)}
                    endIcon={showJoinFields ? <ExpandLess /> : <ExpandMore />}
                  >
                    Join Room
                  </Button>
                </StyledListItem>

                <Collapse in={showJoinFields} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter Room Code"
                      value={roomCode}
                      onChange={(e) => setRoomCode(e.target.value)}
                    />
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={onJoinRoom}
                      >
                        Join
                      </Button>
                  </Box>
                </Collapse>
              </List>
            </>
          )}

          <Divider sx={{ my: 2 }}>Players</Divider>

          <Box sx={{ mb: 2 }}>
            <PlayersList dense>
              {connectedUsers.map((user, index) => (
                <StyledListItem key={index}>
                  <ListItemText 
                    primary={user.name}
                    primaryTypographyProps={{
                      fontFamily: 'MedievalSharp',
                      fontSize: '1.1rem'
                    }}
                  />
                  {user.isCreator && (
                    <CreatorBadge>
                      <StarIcon />
                      <Typography>DM</Typography>
                    </CreatorBadge>
                  )}
                  {user.isPresenter && (
                    <PresenterBadge>
                      <PresenterIcon />
                      <Typography>Presenter</Typography>
                    </PresenterBadge>
                  )}
                </StyledListItem>
              ))}
            </PlayersList>
          </Box>
        </ContentSection>
      </DrawerContent>
    </StyledDrawer>
  );
};

export default LobbyDrawer;

